import { A2uiMessage } from '@a2ui/web_core/v0_9';

import { SEARCH_TYPES } from 'components/DeepSearch/constants';

import { DeepSearchMessage } from '../types';

export function parseA2uiBlock(
  text: string
): { before: string; after: string; messages: A2uiMessage[] } | null {
  // Both fences may carry the `a2ui` tag either before or after the
  // backticks (```a2ui, a2ui```, or just ```). Treating it as optional on
  // each side keeps the tag from being left over in the `before`/`after`
  // text when the agent places or repeats it on a fence.
  const match = text.match(
    /(?:a2ui)?```(?:a2ui)?\r?\n([\s\S]*?)\r?\n(?:a2ui)?```(?:a2ui)?/
  );
  if (!match) {
    return null;
  }

  const messages: A2uiMessage[] = [];
  for (const line of match[1].split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    try {
      messages.push(JSON.parse(trimmed) as A2uiMessage);
    } catch {
      // LLMs occasionally escape quotes inside JSON (e.g. \" instead of ").
      // Try stripping the errant backslash-escaping before giving up.
      try {
        messages.push(JSON.parse(trimmed.replace(/\\"/g, '"')) as A2uiMessage);
      } catch {
        console.warn('[a2ui] Failed to parse block line', {
          lineLength: trimmed.length,
        });
      }
    }
  }

  if (messages.length === 0) {
    return null;
  }

  // Calculate the position of the end of the block to get the next steps text
  const blockStart = match.index ?? 0;
  const blockEnd = blockStart + match[0].length;

  return {
    before: text.slice(0, blockStart).trim(),
    after: text.slice(blockEnd).trim(),
    messages,
  };
}

// Parse newline-delimited JSON lines from an a2ui string (e.g. from a
// functionResponse). Applies the same quote-unescape fallback as parseA2uiBlock.
export function parseA2uiString(a2uiStr: string): A2uiMessage[] {
  const messages: A2uiMessage[] = [];
  for (const line of a2uiStr.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    try {
      messages.push(JSON.parse(trimmed) as A2uiMessage);
    } catch {
      try {
        messages.push(JSON.parse(trimmed.replace(/\\"/g, '"')) as A2uiMessage);
      } catch {
        console.warn('[a2ui] Failed to parse stream line', {
          lineLength: trimmed.length,
        });
      }
    }
  }
  return messages;
}

export function buildAssistantMessages(
  text: string,
  a2uiMessages?: A2uiMessage[]
): DeepSearchMessage[] {
  // Preferred path: agent uses [a2ui] placeholder + caller supplies pre-extracted messages.
  if (a2uiMessages && a2uiMessages.length > 0 && text.includes('[a2ui]')) {
    const [before, after] = text.split('[a2ui]');
    const result: DeepSearchMessage[] = [];
    if (before.trim()) {
      result.push({
        id: crypto.randomUUID(),
        data: before.trim(),
        role: 'assistant',
      });
    }
    result.push({
      id: crypto.randomUUID(),
      data: a2uiMessages,
      role: 'assistant',
    });
    if (after.trim()) {
      result.push({
        id: crypto.randomUUID(),
        data: after.trim(),
        role: 'assistant',
      });
    }
    return result;
  }

  // Fallback: regex extraction from ```a2ui``` code blocks (backward compat).
  const parsed = parseA2uiBlock(text);
  if (parsed) {
    const result: DeepSearchMessage[] = [];
    if (parsed.before) {
      result.push({
        id: crypto.randomUUID(),
        data: parsed.before,
        role: 'assistant',
      });
    }
    result.push({
      id: crypto.randomUUID(),
      data: parsed.messages,
      role: 'assistant',
    });
    if (parsed.after) {
      result.push({
        id: crypto.randomUUID(),
        data: parsed.after,
        role: 'assistant',
      });
    }
    return result;
  }
  return [{ id: crypto.randomUUID(), data: text, role: 'assistant' }];
}

export function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return Boolean(value);
}

// ---- relocated from shared/chat ----
export const isUnknown = (value: string) =>
  value.toLowerCase() === 'unknown' ||
  value === '' ||
  value === 'null' ||
  value === 'undefined' ||
  value.toLowerCase() === 'not found';

export const mapReportTypeFromRecentReportsToChatType = (
  reportType: string
): string | null => {
  const typeMap: Record<string, string> = {
    detailed_person_report: SEARCH_TYPES.PERSON,
    reverse_phone_report: SEARCH_TYPES.PHONE,
    property_report: SEARCH_TYPES.PROPERTY,
    vehicle_report: SEARCH_TYPES.VEHICLE,
    social_network_report: SEARCH_TYPES.EMAIL,
    obituary_report: SEARCH_TYPES.OBITUARY,
    ancestry_report: SEARCH_TYPES.ANCESTOR,
    contact_report: SEARCH_TYPES.CONTACT,
    username_report: SEARCH_TYPES.USERNAME,
  };

  const mappedType = typeMap[reportType];

  if (Object.values(SEARCH_TYPES).includes(reportType as any)) {
    return reportType;
  }

  return mappedType || null;
};
